import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { acceptInvite, getInvite, type InvitePreview } from "../api";
import { LanguageSwitcher } from "../i18n/LanguageSwitcher";

// Public page reached from the invite e-mail link (/accept-invite?token=...).
// The single-use token is the credential: we preview it (to show who/which firm
// the account is for), let the invitee set a password, then bounce to /login.
export function AcceptInvitePage() {
  const { t } = useTranslation(["dashboard", "common"]);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError(t("invite.missingToken"));
      setLoading(false);
      return;
    }
    let cancelled = false;
    getInvite(token)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t("invite.passwordTooShort"));
      return;
    }
    if (password !== confirm) {
      setError(t("invite.passwordMismatch"));
      return;
    }
    setSubmitting(true);
    try {
      await acceptInvite(token, password);
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <img src="/logo-mark.png" alt="AICE" className="login-logo" />
        <h1 className="login-title">{t("invite.title")}</h1>

        {loading && <p className="muted">{t("common:actions.loading")}</p>}

        {!loading && loadError && <p className="login-error">{loadError}</p>}

        {!loading && !loadError && done && (
          <p className="info">{t("invite.success")}</p>
        )}

        {!loading && !loadError && !done && preview && (
          <>
            <p className="muted">
              {t("invite.intro", {
                username: preview.username,
                firm: preview.firm_name ?? preview.firm_id,
              })}
            </p>
            {error && <p className="login-error">{error}</p>}
            <form className="login-form" onSubmit={onSubmit}>
              <input
                className="login-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("invite.newPassword")}
                autoComplete="new-password"
                required
              />
              <input
                className="login-input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={t("invite.confirmPassword")}
                autoComplete="new-password"
                required
              />
              <button className="login-button" type="submit" disabled={submitting}>
                {submitting ? t("common:actions.loading") : t("invite.submit")}
              </button>
            </form>
          </>
        )}

        <div className="login-lang">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  );
}
